import { useState } from 'react';
import { Flag, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { createNegotiationReport } from '@/services/negotiationCredits';
import { REPORT_CATEGORY_LABELS, type ReportCategory } from '@/types/negotiationCredits';

export interface ReportNegotiationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  negotiationId: string;
  reportedUserId: string;
  reportedUserName?: string;
}

export default function ReportNegotiationModal({
  open,
  onOpenChange,
  negotiationId,
  reportedUserId,
  reportedUserName = 'este usuário',
}: ReportNegotiationModalProps) {
  const [category, setCategory] = useState<ReportCategory | ''>('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!category) {
      toast.error('Selecione um motivo para a denúncia');
      return;
    }

    try {
      setLoading(true);
      await createNegotiationReport(
        negotiationId,
        reportedUserId,
        category,
        details || undefined
      );

      toast.success('Denúncia enviada com sucesso. Nossa equipe irá analisar.');
      onOpenChange(false);
      setCategory('');
      setDetails('');
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar denúncia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-display text-foreground flex items-center gap-2">
            <Flag className="w-5 h-5 text-destructive" />
            Denunciar Usuário
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Denunciar <span className="font-medium text-foreground">{reportedUserName}</span> por comportamento inadequado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Category Select */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Motivo da denúncia *
            </label>
            <Select value={category} onValueChange={(v) => setCategory(v as ReportCategory)}>
              <SelectTrigger className="bg-secondary/50 border-border">
                <SelectValue placeholder="Selecione um motivo..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(REPORT_CATEGORY_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Details Textarea */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Detalhes (opcional)
            </label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Descreva o que aconteceu com mais detalhes..."
              className="bg-secondary/50 border-border resize-none"
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-right">
              {details.length}/1000
            </p>
          </div>

          {/* Info Box */}
          <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
            <div className="flex gap-2">
              <AlertTriangle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <p className="text-xs text-foreground">
                Nossa equipe irá analisar a denúncia e tomar as medidas cabíveis. Denúncias falsas podem resultar em penalidades.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!category || loading}
              className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Flag className="w-4 h-4 mr-2" />
                  Enviar Denúncia
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
